import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartVoucherBlockConfig } from '@front-platform/content-builder';
import { cartVoucherBlockResolveConfig } from '@front-platform/content-builder';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Component, Inject, OnDestroy, OnInit, input } from '@angular/core';

import { CartKeys } from '@config/keys/cart.keys';

import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { ModalsService } from '@core/services/modals/modals.service';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';

import { CartVoucherModalComponent } from '@shared/modals/cart-voucher-modal/cart-voucher-modal.component';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

import {
  CART_VOUCHER_BLOCK_DATA_SOURCE,
  CartVoucherBlockDataSource,
} from './cart-voucher-block-view-model';
import { provideCartVoucherBlockDataSource } from './cart-voucher-block-data-source.providers';
export interface CartVoucherBlockRuntimeConfig {}

export type CartVoucherBlockConfigInput = Partial<
  Omit<CartVoucherBlockConfig, keyof CartVoucherBlockRuntimeConfig> & CartVoucherBlockRuntimeConfig
>;
export type CartVoucherBlockResolvedConfig = Omit<
  CartVoucherBlockConfig,
  keyof CartVoucherBlockRuntimeConfig
> &
  Partial<CartVoucherBlockRuntimeConfig>;
export interface CartVoucherBlockContract extends ConfigComponentContract<
  CartVoucherBlockConfigInput,
  CartVoucherBlockResolvedConfig
> {}

@Component({
  selector: 'app-cart-voucher-block',
  templateUrl: './cart-voucher-block.component.html',
  standalone: false,
  providers: [...provideCartVoucherBlockDataSource()],
})
export class CartVoucherBlockComponent implements OnInit, OnDestroy, CartVoucherBlockContract {
  readonly config = input<CartVoucherBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartVoucherBlockConfig,
    CartVoucherBlockRuntimeConfig
  >(cartVoucherBlockResolveConfig, () => ({ config: this.config() }));
  get headingColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor;
  }

  protected readonly cartVoucherVm$;

  isValidVoucher$ = this.orderSelector.selectValidation$.pipe(
    map(validation => validation?.coupons?.[0]?.valid ?? false),
  );

  private readonly destroyed$ = new Subject<boolean>();

  constructor(
    private cartDispatcher: CartStoreDispatcher,
    private readonly modalsService: ModalsService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly orderSelector: OrderStoreSelector,
    @Inject(CART_VOUCHER_BLOCK_DATA_SOURCE)
    blockDataSource: CartVoucherBlockDataSource,
  ) {
    this.cartVoucherVm$ = blockDataSource.vm$;
  }

  ngOnInit(): void {
    this.isValidVoucher$.pipe(takeUntil(this.destroyed$)).subscribe(v => {
      if (v) {
        this.modalsService.close(CartVoucherModalComponent.handle);
      }
    });
  }

  openInputVoucher(): void {
    this.modalsService.open(CartVoucherModalComponent.handle);
  }

  ngOnDestroy(): void {
    this.modalsService.close(CartVoucherModalComponent.handle);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onRemoveVoucher(_event: Event): void {
    this.cartDispatcher.updateActive({ [CartKeys.Coupons]: [] });
    // TODO: Check if this is unsubscribed correctly when done
    this.orderDispatcher.checkCartIsValid();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartVoucherBlockComponent, { 'keyAliases': {} });
